/**
 * 레거시 commissionTemplates 엔진 연동 — resolveCommissionTemplate + extract
 */
import { normalizeCommissionDrugCode } from '@/pages/settlement-fees/commissionDrugCode';
import { resolveCommissionTemplate } from '@/pages/settlement-fees/commission-templates/index.js';
import {
  workbookToSheetMergesMap,
  workbookToSheetRowsMap,
} from '@/pages/settlement-fees/sheetRowReader';
import type { TemplateExtractRow } from '@/pages/settlement-fees/commissionTemplateTypes';
import * as XLSX from 'xlsx';

interface CommissionRecord {
  drugCode?: string;
  drugName?: string;
  pharmaceuticalCompanyName?: string;
  price?: number | null;
  standardCommissionRate?: number | null;
  standardCommissionNote?: string;
  referenceYearMonth?: string;
  templateId?: string;
}

const toPercentRate = (value: number | null | undefined): number => {
  if (value == null || !Number.isFinite(value)) return 0;
  return value <= 1 ? value * 100 : value;
};

const mapRecordToRow = (
  record: CommissionRecord,
  referenceYearMonth: string,
  sourceFile: string,
): TemplateExtractRow => ({
  pharmaceuticalCompanyName: record.pharmaceuticalCompanyName ?? '',
  drugCode: normalizeCommissionDrugCode(record.drugCode),
  drugName: String(record.drugName ?? '').trim(),
  standardCommissionRate: toPercentRate(record.standardCommissionRate),
  commissionNote: String(record.standardCommissionNote ?? '').trim(),
  price: Number(record.price) || 0,
  templateId: record.templateId ?? '',
  referenceYearMonth: record.referenceYearMonth || referenceYearMonth,
  sourceFile,
});

export const extractTemplateFile = async (
  file: File,
  referenceYearMonth: string,
): Promise<{
  rows: TemplateExtractRow[];
  templateId?: string;
} | null> => {
  const arrayBuffer = await file.arrayBuffer();
  const workbook = XLSX.read(arrayBuffer, {
    type: 'array',
    cellDates: false,
    raw: false,
  });

  const context = {
    file,
    workbook,
    sheetRowsMap: workbookToSheetRowsMap(workbook, { defval: null }),
    sheetMergesMap: workbookToSheetMergesMap(workbook),
    headerProfiles: [] as unknown[],
  };

  const matchedTemplate = resolveCommissionTemplate(context);
  if (!matchedTemplate) return null;

  const templateResult = matchedTemplate.extract(context, {
    referenceYearMonth,
  });
  const records = (templateResult.rows ?? []) as CommissionRecord[];
  if (!records.length) return null;

  return {
    templateId: matchedTemplate.id ?? matchedTemplate.pharmaceuticalCompanyName,
    rows: records
      .filter((record) => String(record.drugName ?? '').trim())
      .map((record) => mapRecordToRow(record, referenceYearMonth, file.name)),
  };
};

export { resolveCommissionTemplate };
